// ConnectionRow v0.3 — one platform row inside the ConnectionAuthDrawer.
//
// @version v0.3 (2026-04-28)
//   v0.3: + Honest availability states. Disabled rows now switch on
//         config.availability with 4 distinct labels and tooltips:
//         live | coming_soon | pending_review | unavailable_upstream.
//         Empty is better than wrong — users no longer waste cycles on
//         dead Connect buttons.
//   v0.2: + Agenda labels for multi-account flows. When 2+ connections
//         exist on the same platform, an inline prompt asks "what's this
//         account for?" with 4 quick-picks (🏢 Brand / 👤 Personal /
//         🎯 Topic / 🗂 Misc) + free-text custom. Selection persists to
//         user_connections.metadata.agenda_label + agenda_set_at. The
//         label renders as a small blue badge after the handle. Click
//         the badge to edit. NO schema migration — uses existing metadata
//         jsonb column.
//   v0.1: original ship.
//
// Five visual states (driven by props rather than internal flags so the
// drawer parent is the single source of truth):
//
//   disconnected   ⊕ icon + 2-line copy + [Connect]
//   connecting     spinner + "Opening <platform>…"
//   connected      ✓ + handle + last-sync time + [Disconnect]
//                  (multi-account: list of handles, [Set primary] / [Disconnect]
//                   per row, plus [Connect another] at the bottom)
//   error          ⚠ + error text + [Retry]
//   disabled       grayed + [Coming next week] tooltip on hover (Tier 2/3)
//
// Twitter multi-account flow inlines an instruction set inside the row
// (modal-in-drawer would be poor UX — we expand in place instead).
//
// Tooltips on every interactive element per FOX-BIBLE rule.

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Plus, Check, AlertCircle, RefreshCw, X, Star, Lock, Hourglass, Info, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { PlatformConfig, PlatformCopy } from "@/lib/connectionConfigs";

export interface UserConnection {
  id: string;
  user_id: string;
  platform: string;
  account_handle: string | null;
  account_id: string | null;
  is_primary: boolean;
  scopes: string[] | null;
  connected_at: string | null;
  last_synced_at: string | null;
  disconnected_at: string | null;
  metadata?: { agenda_label?: string; agenda_set_at?: string; [k: string]: any } | null;
}

const AGENDA_PRESETS: { key: string; emoji: string; label: string }[] = [
  { key: "brand", emoji: "🏢", label: "Brand" },
  { key: "personal", emoji: "👤", label: "Personal/Founder" },
  { key: "topic", emoji: "🎯", label: "Specific topic" },
  { key: "misc", emoji: "🗂", label: "Misc" },
];

function renderAgendaText(label: string): string {
  const preset = AGENDA_PRESETS.find((p) => p.key === label);
  return preset ? `${preset.emoji} ${preset.label.toLowerCase()}` : label;
}

export type RowState = "disconnected" | "connecting" | "connected" | "error" | "disabled";

interface Props {
  platformKey: string;
  config: PlatformConfig;
  copy: PlatformCopy;
  state: RowState;
  connections: UserConnection[];
  errorMessage?: string | null;
  highlight?: boolean; // true when drawer was opened with this row as defaultExpanded
  onConnect: () => void | Promise<void>;
  onConnectAnother?: () => void | Promise<void>;
  onDisconnect: (connectionId: string) => void | Promise<void>;
  onSetPrimary: (connectionId: string) => void | Promise<void>;
  onConnectMedium?: (token: string) => void | Promise<void>;
  onRetry?: () => void;
}

export function ConnectionRow({
  platformKey,
  config,
  copy,
  state,
  connections,
  errorMessage,
  highlight = false,
  onConnect,
  onConnectAnother,
  onDisconnect,
  onSetPrimary,
  onConnectMedium,
  onRetry,
}: Props) {
  const [showTwitterInstructions, setShowTwitterInstructions] = useState(false);
  const [mediumToken, setMediumToken] = useState("");
  const [showMediumInput, setShowMediumInput] = useState(false);
  // Local agenda overrides keyed by connection id — mirrors what we wrote
  // to user_connections.metadata so the badge updates immediately without
  // waiting for parent re-fetch.
  const [agendaOverrides, setAgendaOverrides] = useState<Record<string, string>>({});
  const [editingAgendaId, setEditingAgendaId] = useState<string | null>(null);
  const [agendaCustom, setAgendaCustom] = useState("");
  const [savingAgendaId, setSavingAgendaId] = useState<string | null>(null);
  const Icon = config.icon;

  // Determine which connection (if any) needs an agenda label prompt:
  // multi-account on same platform AND at least one connection has no
  // agenda label set (in DB or in local override).
  function effectiveAgenda(c: UserConnection): string | null {
    if (agendaOverrides[c.id] !== undefined) return agendaOverrides[c.id] || null;
    return c.metadata?.agenda_label || null;
  }
  const needsAgendaPromptId = useMemo(() => {
    if (!config.multiAccount) return null;
    if (connections.length < 2) return null;
    const unlabeled = connections.find((c) => !effectiveAgenda(c));
    return unlabeled?.id || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections, config.multiAccount, agendaOverrides]);

  // Open the editor inline when a multi-account row needs labeling and
  // the user hasn't explicitly opened a different one.
  useEffect(() => {
    if (needsAgendaPromptId && !editingAgendaId) {
      setEditingAgendaId(needsAgendaPromptId);
    }
  }, [needsAgendaPromptId, editingAgendaId]);

  async function setAgenda(connectionId: string, label: string) {
    const trimmed = label.trim().slice(0, 80);
    if (!trimmed) return;
    setSavingAgendaId(connectionId);
    try {
      const target = connections.find((c) => c.id === connectionId);
      const existingMeta = (target?.metadata && typeof target.metadata === "object") ? target.metadata : {};
      const nextMeta = { ...existingMeta, agenda_label: trimmed, agenda_set_at: new Date().toISOString() };
      const { error } = await supabase
        .from("user_connections")
        .update({ metadata: nextMeta })
        .eq("id", connectionId);
      if (error) {
        console.warn("[ConnectionRow] setAgenda failed", error);
        return;
      }
      setAgendaOverrides((prev) => ({ ...prev, [connectionId]: trimmed }));
      setEditingAgendaId(null);
      setAgendaCustom("");
    } finally {
      setSavingAgendaId(null);
    }
  }

  const isConnected = connections.length > 0;
  const isMedium = platformKey === "medium";
  const isTwitter = platformKey === "twitter";

  const handleConnectClick = () => {
    if (isMedium) {
      setShowMediumInput(true);
      return;
    }
    if (isTwitter && isConnected) {
      // Multi-account second+ connection: show inline swap instructions.
      setShowTwitterInstructions(true);
      return;
    }
    onConnect();
  };

  const handleConnectAnotherClick = () => {
    if (isTwitter) {
      setShowTwitterInstructions(true);
    } else {
      onConnectAnother?.();
    }
  };

  return (
    <div
      data-platform={platformKey}
      className={[
        "rounded-xl border p-3 transition-colors",
        highlight ? "border-primary/40 bg-primary/5 ring-1 ring-primary/30" : "border-border/60 bg-background/40",
        state === "disabled" ? "opacity-60" : "",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-muted/30 shrink-0 flex items-center justify-center">
          <Icon className="h-4 w-4 text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground">{config.name}</span>
            {state === "connected" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-300 border-emerald-500/30 cursor-help">
                    <Check className="h-2.5 w-2.5 mr-0.5" /> connected
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[260px]">
                  Read-only access is active. Last sync: {connections[0]?.last_synced_at ? new Date(connections[0].last_synced_at).toLocaleString() : "just now"}.
                </TooltipContent>
              </Tooltip>
            )}
            {state === "disabled" && config.availability === "pending_review" && (
              <span className="inline-flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-200 border-amber-500/30 cursor-help">
                      <Hourglass className="h-2.5 w-2.5 mr-0.5" /> Pending review
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs max-w-[280px]">
                    {config.pendingReviewMessage || "Awaiting platform approval"}
                  </TooltipContent>
                </Tooltip>
              </span>
            )}
            {state === "disabled" && config.availability === "coming_soon" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-[9px] bg-muted/30 text-muted-foreground border-border/60 cursor-help">
                    Coming soon
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[280px]">
                  {config.comingSoonMessage || "OAuth integration coming soon"}
                </TooltipContent>
              </Tooltip>
            )}
            {state === "disabled" && config.availability === "unavailable_upstream" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-[9px] bg-muted/30 text-muted-foreground border-border/60 cursor-help">
                    Coming back v1.2
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[280px]">
                  Platform API restrictions — re-enabling in v1.2
                </TooltipContent>
              </Tooltip>
            )}
            {state === "disabled" && !config.availability && config.pendingReview && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-200 border-amber-500/30 cursor-help">
                    <Hourglass className="h-2.5 w-2.5 mr-0.5" /> Pending review
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[280px]">
                  {config.pendingReviewMessage || "Awaiting platform approval"}
                </TooltipContent>
              </Tooltip>
            )}
            {state === "disabled" && !config.availability && !config.pendingReview && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-[9px] bg-muted/30 text-muted-foreground border-border/60 cursor-help">
                    Coming next week
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[260px]">
                  This platform is on the next-up list. We're staging the OAuth app and rolling it out next week.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className="text-[12px] text-muted-foreground mt-0.5">{copy.whatYouGet}</p>
          <p className="text-[11px] text-muted-foreground/80 italic mt-1">{copy.cohortSignal}</p>

          {/* Privacy floor — quiet, always present, identical across archetypes. */}
          <div className="mt-1.5 flex items-start gap-1 text-[10px] text-muted-foreground/70">
            <Lock className="h-2.5 w-2.5 mt-0.5 shrink-0" />
            <span>{config.privacyFloor}</span>
          </div>

          {/* ── Connected list (single or multi) ──────────────────── */}
          {state === "connected" && connections.length > 0 && (
            <ul className="mt-2 space-y-1">
              {connections.map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-xs rounded-md border border-border/40 bg-muted/10 px-2 py-1.5">
                  <Check className="h-3 w-3 text-emerald-300 shrink-0" />
                  <span className="text-foreground font-medium truncate">{c.account_handle || "(handle pending)"}</span>
                  {effectiveAgenda(c) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          variant="outline"
                          className="text-[9px] bg-blue-500/10 text-blue-300 border-blue-500/30 cursor-pointer hover:bg-blue-500/20"
                          onClick={() => setEditingAgendaId(c.id)}
                          title="Edit agenda label"
                        >
                          {renderAgendaText(effectiveAgenda(c)!)}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-[260px]">
                        Agenda: {effectiveAgenda(c)}. Click to edit. We use this label to pick the right account when running sequences or replying to mentions.
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {c.is_primary && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-200 border-amber-500/30 cursor-help">
                          <Star className="h-2.5 w-2.5 mr-0.5" /> primary
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-[240px]">
                        The account we treat as canonical for this platform when surfacing your data.
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <span className="ml-auto flex items-center gap-1">
                    {!c.is_primary && config.multiAccount && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => onSetPrimary(c.id)}>
                            Set primary
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs max-w-[240px]">
                          Make this account the canonical one for this platform.
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-muted-foreground hover:text-destructive"
                          onClick={() => onDisconnect(c.id)}>
                          Disconnect
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-[240px]">
                        Revoke our read-only access. We stop syncing immediately. Once-earned Early Start steps stay earned.
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </li>
              ))}
              {config.multiAccount && (
                <li>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={handleConnectAnotherClick}>
                        <Plus className="h-3 w-3" /> Connect another
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs max-w-[260px]">
                      Add a second account on this platform. We'll show you how to swap accounts before authorizing.
                    </TooltipContent>
                  </Tooltip>
                </li>
              )}
            </ul>
          )}

          {/* ── Agenda label prompt (multi-account, when row needs label) ── */}
          {state === "connected" && editingAgendaId && connections.find((c) => c.id === editingAgendaId) && (
            <div className="mt-2 rounded-md border border-primary/30 bg-primary/5 p-2.5 space-y-2" data-foxchat-id="connection-agenda-prompt-v0.2">
              <div className="flex items-center gap-1.5">
                <Tag className="h-3 w-3 text-primary" />
                <div className="text-[12px] font-semibold text-foreground">
                  What's <span className="font-mono">{connections.find((c) => c.id === editingAgendaId)?.account_handle || "this account"}</span> for?
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground">
                We'll use this label to make sure you post from the right account when running sequences or replying to mentions.
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {AGENDA_PRESETS.map((p) => (
                  <Tooltip key={p.key}>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px]"
                        disabled={savingAgendaId === editingAgendaId}
                        onClick={() => setAgenda(editingAgendaId, p.key)}
                      >
                        {p.emoji} {p.label}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">{p.label} agenda</TooltipContent>
                  </Tooltip>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <Input
                  value={agendaCustom}
                  onChange={(e) => setAgendaCustom(e.target.value)}
                  placeholder="Or type a custom label (e.g. 'automation')..."
                  className="text-[11px] h-7"
                  maxLength={80}
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      className="h-7 text-[11px]"
                      disabled={!agendaCustom.trim() || savingAgendaId === editingAgendaId}
                      onClick={() => setAgenda(editingAgendaId, agendaCustom.trim())}
                    >
                      {savingAgendaId === editingAgendaId ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">Save custom agenda label.</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => { setEditingAgendaId(null); setAgendaCustom(""); }}>
                      Skip
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">Skip for now. You can edit later by clicking the agenda badge.</TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}

          {/* ── Twitter multi-account inline instructions ─────────── */}
          {showTwitterInstructions && (
            <div className="mt-2 rounded-md border border-primary/30 bg-primary/5 p-2.5 text-[11px] space-y-1.5">
              <div className="text-foreground font-semibold">Have multiple X accounts? Here's how:</div>
              <ol className="space-y-1 text-muted-foreground list-decimal list-inside">
                <li>Open x.com in a new browser tab.</li>
                <li>Log out of any account currently signed in.</li>
                <li>Log into the X account you want to connect here.</li>
                <li>Click <span className="text-foreground">Connect X account</span> below.</li>
                <li>Come back to this page after authorization.</li>
              </ol>
              <div className="flex items-center gap-2 pt-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" className="h-7 text-[11px] gap-1.5" onClick={() => { setShowTwitterInstructions(false); onConnect(); }}>
                      Connect X account
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs max-w-[240px]">Opens the X authorization page in this same tab. We'll bring you back when done.</TooltipContent>
                </Tooltip>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setShowTwitterInstructions(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* ── Medium token paste inline ─────────────────────────── */}
          {showMediumInput && state !== "connected" && (
            <div className="mt-2 rounded-md border border-primary/30 bg-primary/5 p-2.5 text-[11px] space-y-2">
              <div className="text-foreground font-semibold">Paste your Medium Integration Token</div>
              <ol className="space-y-1 text-muted-foreground list-decimal list-inside">
                <li>Go to <span className="text-foreground">medium.com/me/settings</span>.</li>
                <li>Scroll to <span className="text-foreground">Integration tokens</span>.</li>
                <li>Create a token, copy it, paste below.</li>
              </ol>
              <input
                type="password"
                value={mediumToken}
                onChange={(e) => setMediumToken(e.target.value)}
                placeholder="2a1c... (Medium integration token)"
                className="w-full rounded border border-border/60 bg-background/80 px-2 py-1 text-[12px] font-mono text-foreground"
                autoComplete="off"
              />
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" className="h-7 text-[11px]"
                      disabled={!mediumToken.trim()}
                      onClick={async () => {
                        if (!onConnectMedium) return;
                        await onConnectMedium(mediumToken.trim());
                        setMediumToken("");
                        setShowMediumInput(false);
                      }}>
                      Validate and save
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs max-w-[240px]">We send the token to Medium's /v1/me endpoint to confirm it's valid before storing.</TooltipContent>
                </Tooltip>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => { setShowMediumInput(false); setMediumToken(""); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* ── Action button row ─────────────────────────────────── */}
          {state === "disconnected" && !showTwitterInstructions && !showMediumInput && (
            <div className="mt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" className="h-7 text-[11px] gap-1.5" disabled={!config.enabled} onClick={handleConnectClick}>
                    <Plus className="h-3 w-3" /> Connect
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[260px]">
                  {config.enabled
                    ? "Opens the platform's authorization page in this same tab. We come back here when you're done."
                    : "This platform is coming next week — we're staging the OAuth app rollout."}
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {state === "connecting" && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Opening {config.name}…</span>
            </div>
          )}

          {state === "error" && (
            <div className="mt-2 rounded-md border border-red-500/30 bg-red-500/5 p-2 text-[11px] text-red-200/90 flex items-start gap-2">
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div>{errorMessage || "Something went wrong. Try again."}</div>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => onRetry?.()}>
                        <RefreshCw className="h-2.5 w-2.5" /> Retry
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs max-w-[240px]">Re-initiate the connection. We start the authorization flow fresh.</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}

          {state === "disabled" && config.availability === "pending_review" && (
            <div className="mt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1.5 border-amber-500/30 text-amber-200" disabled>
                    <Hourglass className="h-3 w-3" /> Pending review
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[280px]">
                  {config.pendingReviewMessage || "Awaiting platform approval"}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          {state === "disabled" && config.availability === "coming_soon" && (
            <div className="mt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1.5" disabled>
                    <Hourglass className="h-3 w-3" /> Coming soon
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[280px]">
                  {config.comingSoonMessage || "OAuth integration coming soon"}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          {state === "disabled" && config.availability === "unavailable_upstream" && (
            <div className="mt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1.5" disabled>
                    <X className="h-3 w-3" /> Coming back v1.2
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[280px]">
                  Platform API restrictions — re-enabling in v1.2
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          {state === "disabled" && !config.availability && config.pendingReview && (
            <div className="mt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1.5 border-amber-500/30 text-amber-200" disabled>
                    <Hourglass className="h-3 w-3" /> Pending review
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[280px]">
                  {config.pendingReviewMessage || "Awaiting platform approval"}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          {state === "disabled" && !config.availability && !config.pendingReview && (
            <div className="mt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1.5" disabled>
                    <X className="h-3 w-3" /> Coming next week
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-[260px]">
                  We'll enable this row when the OAuth app is staged. No action needed from you.
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
